import { useEffect, useRef, useState } from "react";
import type { Video } from "@/types/video";
import { formatDuration } from "@/utils/video";

export default function VideoPlayer({ video }: { video: Video }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(70);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= video.duration) {
          setPlaying(false);
          return video.duration;
        }
        return prev + 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [playing, video.duration]);

  const percent = video.duration ? (progress / video.duration) * 100 : 0;

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = barRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setProgress(Math.round(ratio * video.duration));
  };

  const toggle = () => {
    if (progress >= video.duration) setProgress(0);
    setPlaying((p) => !p);
  };

  return (
    <div className="w-full">
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground-950">
        <img
          src={video.cover}
          alt={video.title}
          title={`${video.title} 糖豆视频`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/20"></div>

        <span className="absolute top-3 left-3 px-2 py-1 rounded-md bg-foreground-950/60 text-background-100 text-xs flex items-center gap-1">
          <span className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-movie-line"></i>
          </span>
          演示播放器
        </span>

        {!playing && (
          <button
            type="button"
            aria-label="播放"
            onClick={toggle}
            className="absolute inset-0 flex items-center justify-center group cursor-pointer"
          >
            <span className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-3xl md:text-4xl group-hover:bg-primary-600 transition-colors">
              <i className="ri-play-fill"></i>
            </span>
          </button>
        )}

        <div className="absolute inset-x-0 bottom-0 px-3 md:px-4 py-3">
          <div
            ref={barRef}
            onClick={seek}
            className="group/bar py-2 cursor-pointer"
            role="presentation"
          >
            <div className="h-1.5 rounded-full bg-background-50/30 overflow-hidden">
              <div className="h-full bg-primary-500" style={{ width: `${percent}%` }}></div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label={playing ? "暂停" : "播放"}
              onClick={toggle}
              className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50/20 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer"
            >
              <i className={playing ? "ri-pause-fill text-xl" : "ri-play-fill text-xl"}></i>
            </button>
            <span className="text-xs font-label text-background-100 tabular-nums">
              {formatDuration(progress)} / {formatDuration(video.duration)}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-background-100">
                <i className={volume === 0 ? "ri-volume-mute-line" : "ri-volume-up-line"}></i>
              </span>
              <input
                type="range"
                min={0}
                max={100}
                value={volume}
                aria-label="音量"
                onChange={(e) => setVolume(Number(e.target.value))}
                className="w-20 md:w-28 accent-primary-500 cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>
      <p className="mt-2 text-xs text-foreground-400 flex items-center gap-1.5">
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-information-line"></i>
        </span>
        页面为播放控制界面演示，未接入真实视频流。
      </p>
    </div>
  );
}